import React, { useEffect, useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { Search } from "@mui/icons-material";
import Select from "react-select/";
import moment from "moment";

import {
  USER_ROLE_AGENT,
  USER_ROLE_EMPLOYEE,
  USER_ROLE_FINANCE,
  USER_ROLE_MEMBER,
  USER_ROLE_SUPERAGENT,
} from "../../../common/constants";
import BookingList from "./BookingList";
import excelExport from "../../../utils/excelExport";
import { useDispatch, useSelector } from "react-redux";
import {
  getBookingByUser,
  getBookings,
  getCompanyBookings,
} from "../../../redux2/booking/bookingSlice";
import Loading from "../../../components/Loading";
import convertNumbThousand from "../../../utils/convertNumbThousand";
import StatCard from "../../Billing/StatCard";
import { getCompanyById } from "../../../redux2/user/userSlice";
import { getBookingReport } from "../../../services/report";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonPrimary from "../../../components/Button/ButtonPrimary";

const BookingStat = ({
  user,
  query,
  companies,
  companiesLoading,
  corporate,
}) => {
  const { bookings: bookingsData, isLoading } = useSelector(
    (state) => state.booking
  );
  const { company, isLoading: loading } = useSelector((state) => state.user);

  const [searchParams, setSearchParams] = useSearchParams();

  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState("");
  const [state, setState] = useState("");
  const [payment, setPayment] = useState("");
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [loader, setLoader] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [bookingPerPage, setBookingPerPage] = useState(
    parseInt(searchParams.get("limit")) || 5
  );
  const [channel, setChannel] = useState(
    parseInt(searchParams.get("channel")) || ""
  );
  const [carrier, setCarrier] = useState(
    parseInt(searchParams.get("carrier")) || ""
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  query.page = currentPage || 1;

  const isSuper = [
    USER_ROLE_SUPERAGENT,
    USER_ROLE_EMPLOYEE,
    USER_ROLE_FINANCE,
  ].includes(user?.role);

  const companyId = searchParams.get("company");

  const companyOptions = useMemo(() => {
    return (
      [
        { value: "", label: "All", default: true },
        ...companies?.map((company) => ({
          value: company._id,
          label: company.name,
        })),
      ] ?? []
    );
  }, [companies]);

  useEffect(() => {
    if (companyId && companyOptions.length > 0) {
      const match = companyOptions.find((opt) => opt.value === companyId);
      setSelectedCompany(match || null);
    }
  }, [companyId, companyOptions]);

  useEffect(() => {
    query.status = status.toLowerCase() || "";
    query.search = search || "";
    query.state = state || "";
    query.payment = payment || "";
    query.channel = channel || "";
    query.company = selectedCompany?.value || "";
    query.limit = bookingPerPage || 5;
    query.carrier = carrier || "";
    if (query.status) {
      query.state = "";
    }

    const fetchBookings = () => {
      switch (user?.role) {
        case USER_ROLE_SUPERAGENT:
        case USER_ROLE_EMPLOYEE:
        case USER_ROLE_FINANCE:
          dispatch(getBookings(query));
          break;
        case USER_ROLE_AGENT:
          dispatch(getCompanyBookings(query));
          break;
        default:
          dispatch(getBookingByUser(query));
          break;
      }
    };

    // wait search til 1 sec after user stop typing
    if (search) {
      const timeOutId = setTimeout(() => fetchBookings(), 1000);
      return () => clearTimeout(timeOutId);
    } else {
      fetchBookings();
    }
  }, [
    query,
    dispatch,
    user,
    currentPage,
    status,
    search,
    state,
    payment,
    bookingPerPage,
    selectedCompany,
    channel,
    carrier,
  ]);

  useEffect(() => {
    dispatch(getCompanyById(user?.company));
  }, [dispatch, user]);

  useEffect(() => {
    if (bookingsData?.data) {
      setBookings(bookingsData?.data);
    }
  }, [bookingsData, setBookings]);

  function filterBookings(status) {
    let filteredData;
    if (status === "All") {
      filteredData = bookingsData;
    } else if (status === "Pending") {
      filteredData = {
        ...bookingsData,
        data: bookingsData?.data?.filter(
          (booking) => booking.status === "pending"
        ),
      };
    } else if (status === "Confirmed") {
      filteredData = {
        ...bookingsData,
        data: bookingsData?.data?.filter(
          (booking) => booking.status === "confirmed"
        ),
      };
    } else if (status === "Canceled") {
      filteredData = {
        ...bookingsData,
        data: bookingsData?.data?.filter(
          (booking) => booking.status === "cancelled"
        ),
      };
    }
    setBookings(filteredData);
  }

  function handleChannelChange(e) {
    setChannel(e.value);
    searchParams.set("channel", e.value);
    setSearchParams(searchParams);
  }

  function handleCompanyChange(selectedOption) {
    if (!selectedOption) return;

    setSelectedCompany(selectedOption);

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("company", selectedOption.value);
    setSearchParams(newParams);
  }

  const statData = [
    {
      title: "Wallet Balance",
      data:
        convertNumbThousand(
          company?.wallet || bookings?.[0]?.company?.wallet || 0
        ) + " Birr",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          fill="currentColor"
          className="bi bi-wallet2"
          viewBox="0 0 16 16"
          id="IconChangeColor"
        >
          {" "}
          <path
            d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z"
            id="mainIconPathAttribute"
          ></path>{" "}
        </svg>
      ),
      color: "primary",
    },

    {
      title: "Available Credit",
      data:
        convertNumbThousand(
          company?.creditLimit || bookings?.[0]?.company?.creditLimit
        ) + " Birr",
      // data: "200,000 birr",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          fill="currentColor"
          className="bi bi-credit-card-fill"
          viewBox="0 0 16 16"
          id="IconChangeColor"
        >
          {" "}
          <path
            d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H0V4zm0 3v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7H0zm3 2h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1z"
            id="mainIconPathAttribute"
          ></path>{" "}
        </svg>
      ),
      color: "warning",
    },
  ];

  async function handleExport() {
    setLoader(true);

    let report = await getBookingReport(user?.token, query);

    function getRoute(data) {
      return data?.flight?.routes
        ?.map((route) =>
          route?.flights
            .map((flight, index) => {
              const separator = index === route?.flights.length - 1 ? "" : "-";
              return flight + separator;
            })
            .join("")
        )
        .join("-");
    }

    function mergeRoutes2(flights) {
      let routes = flights?.split("-");
      const uniqueFlights = routes?.filter(
        (flight, index) => flight !== routes[index - 1]
      );

      return uniqueFlights?.join("-");
    }

    const isManual = (booking) => {
      if (
        booking?.channel === "Galileo" ||
        booking?.channel === "NDCManual" ||
        booking?.channel === "FZ"
      ) {
        return true;
      }
      return false;
    };

    const getStatus = (booking) => {
      if (
        booking?.status === "refunded" ||
        booking?.status === "exchanged" ||
        booking?.status === "issued-cancelled" ||
        booking?.status === "exchange-cancelled" ||
        booking?.status === "void-late" ||
        booking?.status === "voided"
      ) {
        return true;
      }
      return false;
    };

    let name = "Bookings " + moment().format("MMM DD, YYYY");
    const data = report?.bookings?.map((booking, index) => {
      return {
        "S/N": index + 1,
        "Issuing Date": moment(booking.createdAt).format("MMM DD, YYYY"),
        Status: getStatus(booking)
          ? booking?.status
          : booking?.status === "confirmed" &&
            booking?.passengers?.status === "pending"
          ? "confirmed"
          : booking?.passengers?.status,
        "Passenger Name":
          booking?.passengers?.firstName + " " + booking?.passengers?.lastName,
        // "Passenger Name":
        //   booking?.passengers?.firstName + " " + booking?.passengers?.lastName,
        "Ticket NO.": booking?.passengers?.ticketNumber || "",
        "New Ticket NO.":
          booking?.flight?.exTicketNumber?.length > 0
            ? booking?.flight?.ticketNumber?.join(", ")
            : "",
        PNR: booking?.flight?.providerPnr ?? booking?.flight?.PNR,
        // "Passenger Passport No.":
        //   booking?.passengers
        //     ?.map((passenger) => passenger?.passportNumber)
        //     ?.join(" - ") || "",
        "Passenger Passport No.": booking?.passengers?.passportNumber || "",
        "Issuing Airline": booking?.flight?.carrier,
        Nationality: booking?.passengers?.nationality || "Ethiopian",
        Sector:
          booking?.flight?.routes?.length > 0
            ? mergeRoutes2(getRoute(booking))
            : booking?.flight?.origin +
              "-" +
              booking?.flight?.destination +
              (isManual(booking) && booking?.flight?.flightType === "Round Trip"
                ? "-" + booking?.flight?.origin
                : ""),
        Type: booking?.passengers?.type || "",
        Currency: booking?.currency || "ETB",
        "System Fare":
          booking?.status !== "voided" || booking?.status !== "void-late"
            ? booking?.type === "exchange" || booking?.type === "refund"
              ? booking?.totalTotalPrice
              : booking?.channel !== "NDC" || booking?.channel !== "GDS"
              ? booking?.passengers?.price > 0
                ? booking?.passengers?.price
                : booking?.flight?.price
              : (booking?.passengers?.price || 0) +
                (booking?.NDCServiceCharge || 0)
            : "-",
        "Sterling SC": booking?.totalServiceCharge,
        // "Agent Fee":
        // booking?.status !== "voided" ? booking?.commission || 0 : 0,
        Total: booking?.totalTotalPrice,
        // "Original Price":
        //   booking?.channel === "GDS" || booking?.channel === "NDC"
        //     ? booking?.totalOriginalPrice
        //     : "-",
        "Fare Difference": booking?.totalExchangeDifference ?? 0,
        "Penalty + Tax": booking.totalTaxAndPenalty ?? 0,
        "Used Fare": booking?.usedFare ?? 0,
        "Refunded Amount": booking?.refundedAmount ?? 0,
        Channel: booking?.channel,
        // Refunded: (booking.penalty || 0) + (booking.tax || 0),
        Payment: booking?.payment,
        "Issuing Company": booking?.companyName || "",
        "Issued By": booking?.agentName || "",
        // Remark: "",
      };
    });

    let result = report?.result?.[0];

    const totalPriceRow = {
      "S/N": "",
      "Issuing Date": "",
      Status: "",
      "Passenger Name": "",
      "Ticket NO.": "",
      PNR: "",
      "Passenger Passport No.": "",
      "Issuing Airline": "",
      Nationality: "",
      Sector: "",
      Type: "",
      Currency: "Grand Total",
      "System Fare": (result?.price || 0) + " Birr",
      "Sterling SC": (result?.serviceCharge || 0) + " Birr",
      // "Agent Fee": (result?.commission || 0) + " Birr",
      Total: (result?.totalAmount || 0) + " Birr",
      // "Original Price": "",
      "Fare Difference": "",
      "Penalty + Tax": "",
      "Used Fare": "",
      "Refunded Amount": "",
      // Refunded: "",
      Payment: "",
      "Issuing Company": "",
      "Issued By": "",
      // Remark: "",
    };

    const updatedData = [...data, totalPriceRow];

    const order = [
      "S/N",
      "Status",
      "Issuing Date",
      "Passenger Name",
      "Ticket NO.",
      "PNR",
      "Passenger Passport No.",
      "Issuing Airline",
      "Nationality",
      "Sector",
      "Type",
      "Currency",
      "System Fare",
      "Sterling SC",
      // "Agent Fee",
      "Total",
      // "Original Price",
      "Fare Difference",
      "Penalty + Tax",
      "Used Fare",
      "Refunded Amount",
      // "Refunded",
      "Payment",
      "Channel",
      "Issuing Company",
      "Issued By",
      // "Remark",
    ];

    excelExport(updatedData, name, true);
    setLoader(false);
  }

  const handleCarrierChange = (e) => {
    setCarrier(e.value);
    searchParams.set("carrier", e.value);
    setSearchParams(searchParams);
  };

  const handleSearch = () => {
    setSearch(searchValue);
  };

  if (isLoading || loading || loader) return <Loading />;

  return (
    <>
      {user?.role === USER_ROLE_MEMBER &&
        statData.map(({ icon, color, title, data }, index) => (
          <StatCard
            key={index}
            icon={icon}
            color={color}
            title={title}
            data={data}
          />
        ))}
      <div className="grid-cols-12">
        <div className="flex flex-wrap mb-4 justify-content-between align-items-center">
          <div className="mt-3 mb-4 card-tabs mt-sm-0 mb-xxl-0">
            <Nav as="ul" className="nav nav-tabs">
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="All"
                  onClick={() => {
                    filterBookings("All");
                    setStatus("");
                  }}
                >
                  All Booking ({bookingsData?.count?.total || 0})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="Pending"
                  onClick={() => {
                    filterBookings("Pending");
                    setStatus("Pending");
                  }}
                >
                  Pending{" "}
                  {!isLoading && "(" + bookingsData?.count?.pending + ")"}
                  {/* {!isLoading && "(" + countBookingLength("Pending") + ")"} */}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="Ticketed"
                  onClick={() => {
                    filterBookings("Confirmed");
                    setStatus(
                      "Confirmed,voided,exchanged,refunded,void-late,exchange-cancelled,issued-cancelled"
                    );
                  }}
                >
                  Ticketed{" "}
                  {!isLoading && "(" + bookingsData?.count?.confirmed + ")"}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="Canceled"
                  onClick={() => {
                    filterBookings("Canceled");
                    setStatus("Cancelled");
                  }}
                >
                  Canceled{" "}
                  {!isLoading && "(" + bookingsData?.count?.cancelled + ")"}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="Partial"
                  onClick={() => {
                    filterBookings("Partial");
                    setState("partial");
                    setStatus("");
                  }}
                >
                  Partial{" "}
                  {!isLoading &&
                    "(" + (bookingsData?.count?.partial || 0) + ")"}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li" className="nav-item">
                <Nav.Link
                  className="nav-link"
                  eventKey="USDCredit"
                  onClick={() => {
                    filterBookings("USDCredit");
                    setPayment("USDCredit");
                    setStatus("");
                  }}
                >
                  USD Bookings
                  {!isLoading &&
                    "(" + (bookingsData?.count?.payment || 0) + ")"}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
          <div className="table-search">
            <div className="mb-4 input-group search-area mb-xxl-0">
              <input
                type="text"
                className="form-control"
                placeholder="Search here"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <span className="input-group-text">
                <button onClick={handleSearch}>
                  <Search size={35} className="text-main" />
                </button>
              </span>
            </div>
          </div>
          <div className="flex justify-between mb-4 w-full">
            <div className="flex gap-2">
              {user?.role !== USER_ROLE_EMPLOYEE &&
                user?.role !== USER_ROLE_MEMBER && (
                  <button
                    className="text-white btn bg-main hover:bg-main-800 border-main-100 mb-xxl-0"
                    onClick={handleExport}
                  >
                    <i className="far fa-file-word me-2"></i>Generate Report
                  </button>
                )}
              <div className="">
                {isSuper && (
                  <ButtonPrimary
                    className="text-black rounded-2xl border-1 border-main bg-slate-50 hover:text-white hover:bg-main-900 focus:outline-none focus:ring-0 mb-xxl-0"
                    onClick={() => {
                      navigate("/half-refund");
                    }}
                  >
                    Half Refund
                  </ButtonPrimary>
                )}
              </div>
            </div>
            {isSuper && (
              <div className="flex flex-col gap-2 justify-end px-2 w-1/2 md:flex-row">
                <Select
                  placeholder={searchParams.get("carrier") ?? "Select Carrier"}
                  value={searchParams.get("carrier")}
                  options={[
                    { value: "", label: "All", default: true },
                    { value: "ET", label: "ET" },
                    { value: "SV", label: "SV" },
                    { value: "KQ", label: "KQ" },
                    { value: "EK", label: "EK" },
                    { value: "QR", label: "QR" },
                    { value: "TK", label: "TK" },
                    { value: "MS", label: "MS" },
                    { value: "FZ", label: "FZ" },
                    { value: "LH", label: "LH" },
                    { value: "AC", label: "AC" },
                  ]}
                  style={{
                    lineHeight: "40px",
                    color: "#7e7e7e",
                    paddingLeft: " 15px",
                  }}
                  className="w-full md:w-1/4"
                  onChange={handleCarrierChange}
                />
                <Select
                  placeholder="Filter by Company"
                  isLoading={companiesLoading}
                  value={selectedCompany}
                  options={companyOptions}
                  isSearchable={true}
                  onChange={handleCompanyChange}
                  // styles={{
                  //   control: (base) => ({
                  //     ...base,
                  //     lineHeight: "22px",
                  //     color: "#7e7e7e",
                  //     paddingLeft: "15px",
                  //   }),
                  // }}
                  style={{
                    lineHeight: "40px",
                    color: "#7e7e7e",
                    paddingLeft: " 15px",
                  }}
                  className="w-full md:w-1/3"
                />
                <Select
                  placeholder={searchParams.get("channel") ?? "Select Channel"}
                  value={searchParams.get("channel")}
                  options={[
                    { value: "", label: "All", default: true },
                    { value: "GDS", label: "GDS" },
                    { value: "NDC", label: "NDC" },
                  ]}
                  style={{
                    lineHeight: "40px",
                    color: "#7e7e7e",
                    paddingLeft: " 15px",
                  }}
                  className="w-full md:w-1/3"
                  onChange={handleChannelChange}
                />
              </div>
            )}
          </div>
        </div>
        <Tab.Content>
          <Tab.Pane eventKey="All">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              utc={bookingsData?.utc}
              corporate={corporate}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="Pending">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              utc={bookingsData?.utc}
              corporate={corporate}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="Ticketed">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              corporate={corporate}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="Canceled">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              corporate={corporate}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="Partial">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              corporate={corporate}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="USDCredit">
            <BookingList
              user={user}
              bookings={bookings?.data || bookings}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={bookingsData?.totalPages}
              bookingPerPage={bookingPerPage}
              setBookingPerPage={setBookingPerPage}
              corporate={corporate}
            />
          </Tab.Pane>
        </Tab.Content>
      </div>
    </>
  );
};

export default BookingStat;
